import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Modal, Input } from '../../components/primitives';
import { ListPage, ListTable, type ListColumn } from '../../components/list';
import { StavbaGate } from '../../components/stavba';
import {
  useKontrolniDny,
  useCreateKontrolniDen,
  useKdShares,
  useAddKdShare,
  useRemoveKdShare,
} from '../../api/hooks';
import { useActiveStavba } from '../stavba-context/StavbaContext';
import type { KontrolniDen } from '../../api/schemas';

export function KontrolniDnyPage() {
  const navigate = useNavigate();
  const { activeStavba } = useActiveStavba();
  const { data: items = [], isLoading, error } = useKontrolniDny(activeStavba?.id ?? null);
  const create = useCreateKontrolniDen(activeStavba?.id ?? null);
  const [shareOpen, setShareOpen] = useState(false);

  async function handleNew() {
    const kd = await create.mutateAsync({ data_json: {} });
    navigate(`/app/kontrolni-dny/${kd.id}`);
  }

  const columns: ListColumn<KontrolniDen>[] = [
    { key: 'cislo', label: 'Č.', width: '90px', mono: true, dim: true, render: (k) => `KD-${String(k.cislo).padStart(2, '0')}` },
    { key: 'datum', label: 'Datum', width: '140px', mono: true, render: (k) => k.datum ? k.datum.slice(0, 10) : '—' },
    { key: 'ukoly_count', label: 'Úkoly', width: '90px', mono: true, align: 'right' },
    {
      key: 'nesplneno',
      label: 'Nesplněno',
      width: '120px',
      render: (k) =>
        k.nesplneno_count > 0 ? (
          <Badge tone="yellow" pill>{k.nesplneno_count}</Badge>
        ) : (
          <span style={{ color: 'var(--sd-text-dimmer)', fontFamily: 'var(--sd-font-mono)' }}>0</span>
        ),
    },
    { key: 'photos_count', label: 'Fotek', width: '90px', mono: true, align: 'right' },
    { key: 'attachments_count', label: 'Příloh', width: '90px', mono: true, align: 'right' },
    {
      key: 'stav',
      label: 'Stav',
      width: '140px',
      render: (k) =>
        k.stav === 'uzavreno' ? (
          <Badge tone="success" pill>✓ Uzavřen</Badge>
        ) : (
          <Badge tone="yellow" pill dot>{k.stav || 'koncept'}</Badge>
        ),
    },
  ];

  if (!activeStavba) {
    return (
      <ListPage breadcrumb={['Realizace', 'Kontrolní dny']} title="Kontrolní dny">
        <div style={{ marginBottom: 14, textAlign: 'right' }}>
          <Button variant="secondary" onClick={() => navigate('/app/kontrolni-dny/nasdilene')}>
            📥 Nasdílené mně
          </Button>
        </div>
        <StavbaGate message="Kontrolní dny se evidují k jedné stavbě." />
      </ListPage>
    );
  }

  return (
    <ListPage
      breadcrumb={['Realizace', activeStavba.nazev, 'Kontrolní dny']}
      title="Kontrolní dny"
      subtitle={isLoading ? 'Načítám…' : error ? 'Chyba' : `${items.length} záznamů`}
      status={isLoading ? 'neutral' : error ? 'draft' : 'done'}
      statusText={isLoading ? 'Načítám…' : error ? 'Chyba' : `${items.length} záznamů`}
      actions={
        <>
          <Button variant="secondary" onClick={() => navigate('/app/kontrolni-dny/nasdilene')}>
            📥 Nasdílené mně
          </Button>
          <Button variant="secondary" onClick={() => setShareOpen(true)}>
            🔗 Sdílet
          </Button>
          <Button variant="primary" onClick={handleNew} disabled={create.isPending}>
            {create.isPending ? 'Zakládám…' : '+ Nový kontrolní den'}
          </Button>
        </>
      }
      searchPlaceholder="Hledat v datu nebo tématu…"
    >
      {error ? (
        <div style={{ padding: 24, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)' }}>
          Chyba: {error instanceof Error ? error.message : 'neznámá'}
        </div>
      ) : (
        <ListTable
          columns={columns}
          rows={items}
          rowKey={(k) => k.id}
          onRowClick={(k) => navigate(`/app/kontrolni-dny/${k.id}`)}
          emptyTitle="Žádné kontrolní dny"
          emptyText="Záznamy z kontrolních dní se objeví zde."
        />
      )}

      <ShareKdModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        stavbaId={activeStavba.id}
        stavbaNazev={activeStavba.nazev}
        kdItems={items}
      />
    </ListPage>
  );
}

/** Modal pro sdílení KD — celá stavba nebo jeden konkrétní KD, jen pro čtení. */
function ShareKdModal({
  open,
  onClose,
  stavbaId,
  stavbaNazev,
  kdItems,
}: {
  open: boolean;
  onClose: () => void;
  stavbaId: number;
  stavbaNazev: string;
  kdItems: KontrolniDen[];
}) {
  const { data: shares = [] } = useKdShares();
  const add = useAddKdShare();
  const remove = useRemoveKdShare();
  const [email, setEmail] = useState('');
  const [scope, setScope] = useState<'stavba' | 'kd'>('stavba');
  const [kdId, setKdId] = useState<number | ''>('');
  const [err, setErr] = useState<string | null>(null);

  const stavbaShares = shares.filter((s) => s.stavba_id === stavbaId);

  async function handleAdd() {
    const e = email.trim();
    if (!e) return;
    if (scope === 'kd' && !kdId) {
      setErr('Vyberte konkrétní kontrolní den.');
      return;
    }
    setErr(null);
    try {
      await add.mutateAsync({
        email: e,
        stavba_id: stavbaId,
        kd_id: scope === 'kd' ? Number(kdId) : null,
      });
      setEmail('');
    } catch (ex: unknown) {
      const status = (ex as { status?: number })?.status;
      setErr(
        status === 404 ? 'Uživatel s tímto e-mailem nenalezen.'
          : status === 409 ? 'Toto sdílení už existuje.'
          : status === 400 ? 'Nelze sdílet sám se sebou.'
          : 'Sdílení se nezdařilo.',
      );
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Sdílet kontrolní dny"
      size="md"
      footer={<Button variant="secondary" onClick={onClose}>Zavřít</Button>}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 12.5, color: 'var(--sd-text-dim)' }}>
          Nasdílený účet uvidí kontrolní dny <strong>jen pro čtení</strong> (nemůže je upravovat).
          Najdou je v sekci „Kontrolní dny → Nasdílené mně".
        </div>

        {/* Rozsah sdílení */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input type="radio" name="kd-share-scope" checked={scope === 'stavba'} onChange={() => setScope('stavba')} />
            Celá stavba <span style={{ color: 'var(--sd-text-dim)' }}>({stavbaNazev}) — všechny KD</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input type="radio" name="kd-share-scope" checked={scope === 'kd'} onChange={() => setScope('kd')} />
            Jen jeden kontrolní den
          </label>
          {scope === 'kd' && (
            <select
              value={kdId}
              onChange={(e) => setKdId(e.target.value ? Number(e.target.value) : '')}
              style={{ padding: '6px 10px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', fontSize: 13, marginLeft: 26 }}
            >
              <option value="">— vyberte KD —</option>
              {kdItems.map((k) => (
                <option key={k.id} value={k.id}>
                  KD-{String(k.cislo).padStart(2, '0')}{k.datum ? ` · ${k.datum.slice(0, 10)}` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* E-mail + sdílet */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e-mail uživatele"
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
            />
          </div>
          <Button variant="primary" onClick={handleAdd} disabled={add.isPending || !email.trim()}>
            {add.isPending ? 'Sdílím…' : 'Sdílet'}
          </Button>
        </div>
        {err && <div style={{ fontSize: 12.5, color: 'var(--sd-danger)' }}>{err}</div>}

        {/* Existující sdílení této stavby */}
        {stavbaShares.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--sd-text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Nasdíleno
            </div>
            {stavbaShares.map((s) => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 10px', background: 'var(--sd-w-f7f4ee, #f7f4ee)', border: '1px solid var(--sd-w-e7e1d4, #e7e1d4)', borderRadius: 4 }}>
                <span style={{ fontSize: 13 }}>
                  <strong>{s.shared_with_name || s.shared_with_email}</strong>
                  {s.shared_with_name && <span style={{ color: 'var(--sd-text-dim)' }}> · {s.shared_with_email}</span>}
                  <span style={{ color: 'var(--sd-text-dim)' }}>
                    {' · '}
                    {s.kd_id === null ? 'celá stavba' : `KD-${String(s.kd_cislo ?? 0).padStart(2, '0')}`}
                  </span>
                </span>
                <Button size="sm" variant="ghost" onClick={() => remove.mutate(s.id)} disabled={remove.isPending}>
                  Zrušit
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
